/**
 * FikoSheetMetalCost - Sac Metal Maliyet Hesaplama Modulu
 *
 * Lazer kesim, plazma kesim, su jeti kesim, zimbalama ve
 * bukme surecleri icin detayli maliyet hesaplama.
 *
 * Hesaplama: Malzeme (sac) + Kesim suresi + Bukme + Delme + Son islem
 */

import { FikoMaterialDB, MaterialProperties, ManufacturingProcessType } from './FikoMaterialDB';
import { CostLineItem } from './FikoCostEngine';

export type SheetMetalCuttingProcess = 'sheet_metal_laser' | 'sheet_metal_plasma' | 'sheet_metal_waterjet' | 'sheet_metal_punching';

export interface SheetMetalCostInput {
    partGeometry: {
        volumeCm3: number;
        surfaceAreaCm2: number;
        boundingBox: { x: number; y: number; z: number };
        complexityScore: number;
    };
    material: MaterialProperties;
    batchSize: number;
    cuttingProcess: SheetMetalCuttingProcess;
    sheetThickness: number;         // mm
    perimeterLength: number;        // mm (kesim cevre uzunlugu)
    bendCount: number;              // Bukum sayisi
    holeCount: number;              // Delik sayisi
    weldingLength?: number;         // mm (kaynak uzunlugu, varsa)
    deburRequired?: boolean;        // Capak alma gerekli mi
    surfaceTreatment?: 'painting' | 'powder_coating' | 'plating' | 'none';
}

export interface SheetMetalCostResult {
    costPerPart: number;
    costPerKg: number;
    breakdown: CostLineItem[];
    cuttingTimeMinutes: number;
    sheetUtilizationPercent: number;
    notes: string[];
    notesTR: string[];
}

export class FikoSheetMetalCost {
    private materialDB: FikoMaterialDB;

    constructor(materialDB: FikoMaterialDB) {
        this.materialDB = materialDB;
    }

    calculate(input: SheetMetalCostInput): SheetMetalCostResult {
        const { material, partGeometry, batchSize, cuttingProcess } = input;
        const notes: string[] = [];
        const notesTR: string[] = [];
        const breakdown: CostLineItem[] = [];

        // 1. Sac malzeme maliyeti
        // Blank boyutu = bounding box XY + 5mm kenar payi
        const blankX = partGeometry.boundingBox.x + 10; // mm
        const blankY = partGeometry.boundingBox.y + 10; // mm
        const blankAreaMM2 = blankX * blankY;
        const blankAreaCm2 = blankAreaMM2 / 100;
        const blankVolumeCm3 = blankAreaCm2 * (input.sheetThickness / 10);
        const blankWeightKg = blankVolumeCm3 * material.density;
        const partWeightKg = partGeometry.volumeCm3 * material.density;
        const sheetPricePerKg = material.sheetPricePerKg || material.pricePerKg * 1.1;
        const materialCost = blankWeightKg * sheetPricePerKg;
        const sheetUtilization = (partWeightKg / blankWeightKg) * 100;

        breakdown.push({
            category: 'material',
            description: `${material.name} sheet ${input.sheetThickness}mm (${blankX.toFixed(0)}x${blankY.toFixed(0)}mm blank, ${blankWeightKg.toFixed(2)}kg)`,
            descriptionTR: `${material.nameTR} sac ${input.sheetThickness}mm (${blankX.toFixed(0)}x${blankY.toFixed(0)}mm blank, ${blankWeightKg.toFixed(2)}kg)`,
            quantity: blankWeightKg,
            unitCost: sheetPricePerKg,
            totalCost: materialCost,
            unit: 'kg'
        });

        // 2. Kesim maliyeti
        const cuttingSpeed = this.getCuttingSpeed(cuttingProcess, material, input.sheetThickness); // mm/min
        const totalCutLength = input.perimeterLength + (input.holeCount * Math.PI * 5); // Delikler ~5mm cap varsayimi
        const cuttingTimeMin = totalCutLength / cuttingSpeed;
        const machineParams = this.materialDB.getMachineParams(cuttingProcess as ManufacturingProcessType);
        const hourlyRate = machineParams?.hourlyRate || 70;
        const cuttingCost = (cuttingTimeMin / 60) * hourlyRate;

        breakdown.push({
            category: 'machine',
            description: `${this.getCuttingProcessName(cuttingProcess)} (${totalCutLength.toFixed(0)}mm at ${cuttingSpeed.toFixed(0)}mm/min = ${cuttingTimeMin.toFixed(1)} min)`,
            descriptionTR: `${this.getCuttingProcessNameTR(cuttingProcess)} (${totalCutLength.toFixed(0)}mm, ${cuttingSpeed.toFixed(0)}mm/dk = ${cuttingTimeMin.toFixed(1)} dk)`,
            quantity: cuttingTimeMin / 60,
            unitCost: hourlyRate,
            totalCost: cuttingCost,
            unit: 'saat'
        });

        // 3. Piercing maliyeti (delik ve kesim baslangic noktasi)
        const pierceCount = input.holeCount + 1; // Her delik + dis profil
        const pierceCostEach = this.getPierceCost(cuttingProcess, input.sheetThickness);
        const pierceCostTotal = pierceCount * pierceCostEach;
        breakdown.push({
            category: 'machine',
            description: `${pierceCount} pierce points at $${pierceCostEach.toFixed(2)} each`,
            descriptionTR: `${pierceCount} delme noktasi, tanesi $${pierceCostEach.toFixed(2)}`,
            quantity: pierceCount,
            unitCost: pierceCostEach,
            totalCost: pierceCostTotal,
            unit: 'adet'
        });

        // 4. Bukme maliyeti
        if (input.bendCount > 0) {
            const bendParams = this.materialDB.getMachineParams('sheet_metal_bending');
            const bendRate = bendParams?.hourlyRate || 40;
            const bendTimePerBend = this.getBendTime(input.sheetThickness, material); // dakika
            const totalBendTime = input.bendCount * bendTimePerBend;
            const bendCost = (totalBendTime / 60) * bendRate;

            breakdown.push({
                category: 'machine',
                description: `${input.bendCount} bend(s) (${bendTimePerBend.toFixed(1)} min each, $${bendRate}/hr)`,
                descriptionTR: `${input.bendCount} bukum (${bendTimePerBend.toFixed(1)} dk/bukum, $${bendRate}/saat)`,
                quantity: input.bendCount,
                unitCost: (bendTimePerBend / 60) * bendRate,
                totalCost: bendCost,
                unit: 'adet'
            });

            // Bukme kalip maliyeti (amorti)
            const bendToolCost = input.bendCount * 50; // $50 per unique bend tool
            const amortizedBendTool = bendToolCost / batchSize;
            breakdown.push({
                category: 'tooling',
                description: `Bend tooling ($${bendToolCost} / ${batchSize} parts)`,
                descriptionTR: `Bukme takimi ($${bendToolCost} / ${batchSize} parca)`,
                quantity: 1,
                unitCost: amortizedBendTool,
                totalCost: amortizedBendTool,
                unit: 'adet'
            });
        }

        // 5. Capak alma / Deburring
        if (input.deburRequired !== false) { // varsayilan: gerekli
            const deburCostPerMeter = 1.5; // $/m
            const deburCost = (totalCutLength / 1000) * deburCostPerMeter;
            breakdown.push({
                category: 'post_processing',
                description: `Deburring (${(totalCutLength / 1000).toFixed(1)}m at $${deburCostPerMeter}/m)`,
                descriptionTR: `Capak alma (${(totalCutLength / 1000).toFixed(1)}m, $${deburCostPerMeter}/m)`,
                quantity: totalCutLength / 1000,
                unitCost: deburCostPerMeter,
                totalCost: deburCost,
                unit: 'metre'
            });
        }

        // 6. Kaynak (varsa)
        if (input.weldingLength && input.weldingLength > 0) {
            const weldRate = 15; // mm/min typical MIG
            const weldTimeMin = input.weldingLength / weldRate;
            const weldHourlyRate = 55;
            const weldCost = (weldTimeMin / 60) * weldHourlyRate;
            breakdown.push({
                category: 'labor',
                description: `Welding (${input.weldingLength.toFixed(0)}mm at ${weldRate}mm/min)`,
                descriptionTR: `Kaynak (${input.weldingLength.toFixed(0)}mm, ${weldRate}mm/dk)`,
                quantity: weldTimeMin / 60,
                unitCost: weldHourlyRate,
                totalCost: weldCost,
                unit: 'saat'
            });
        }

        // 7. Yuzey isleme (varsa)
        if (input.surfaceTreatment && input.surfaceTreatment !== 'none') {
            const surfaceCost = this.getSurfaceTreatmentCost(input.surfaceTreatment, partGeometry.surfaceAreaCm2);
            breakdown.push({
                category: 'post_processing',
                description: `Surface: ${input.surfaceTreatment} (${partGeometry.surfaceAreaCm2.toFixed(0)} cm2)`,
                descriptionTR: `Yuzey: ${this.getSurfaceTreatmentNameTR(input.surfaceTreatment)} (${partGeometry.surfaceAreaCm2.toFixed(0)} cm2)`,
                quantity: partGeometry.surfaceAreaCm2,
                unitCost: surfaceCost / partGeometry.surfaceAreaCm2,
                totalCost: surfaceCost,
                unit: 'cm2'
            });
        }

        // 8. Setup maliyeti (batch'e amorti)
        const setupTime = machineParams?.setupTimeMinutes || 15;
        const setupCost = (setupTime / 60) * hourlyRate / batchSize;
        breakdown.push({
            category: 'setup',
            description: `Setup (${setupTime} min / ${batchSize} parts)`,
            descriptionTR: `Hazirlik (${setupTime} dk / ${batchSize} parca)`,
            quantity: 1,
            unitCost: setupCost,
            totalCost: setupCost,
            unit: 'adet'
        });

        // 9. Iscilik
        const laborMinutes = cuttingTimeMin * 0.2 + input.bendCount * 1.5 + 2; // Handling
        const laborRate = 20;
        const laborCost = (laborMinutes / 60) * laborRate;
        breakdown.push({
            category: 'labor',
            description: `Operator labor (${laborMinutes.toFixed(1)} min)`,
            descriptionTR: `Operator isciligi (${laborMinutes.toFixed(1)} dk)`,
            quantity: laborMinutes / 60,
            unitCost: laborRate,
            totalCost: laborCost,
            unit: 'saat'
        });

        // Toplam
        const costPerPart = breakdown.reduce((sum, item) => sum + item.totalCost, 0);
        const costPerKg = partWeightKg > 0 ? costPerPart / partWeightKg : 0;

        notes.push(`Sheet utilization: ${sheetUtilization.toFixed(0)}%`);
        notes.push(`Cutting time: ${cuttingTimeMin.toFixed(1)} min (${totalCutLength.toFixed(0)}mm perimeter)`);
        notesTR.push(`Sac kullanim orani: %${sheetUtilization.toFixed(0)}`);
        notesTR.push(`Kesim suresi: ${cuttingTimeMin.toFixed(1)} dk (${totalCutLength.toFixed(0)}mm cevre)`);

        if (input.sheetThickness > 20 && cuttingProcess === 'sheet_metal_laser') {
            notes.push('WARNING: Sheet >20mm thick - consider plasma or waterjet');
            notesTR.push('UYARI: Sac >20mm kalin - plazma veya su jeti dusunun');
        }

        return {
            costPerPart: Math.round(costPerPart * 100) / 100,
            costPerKg: Math.round(costPerKg * 100) / 100,
            breakdown,
            cuttingTimeMinutes: cuttingTimeMin,
            sheetUtilizationPercent: sheetUtilization,
            notes,
            notesTR,
        };
    }

    private getCuttingSpeed(process: SheetMetalCuttingProcess, material: MaterialProperties, thickness: number): number {
        // mm/min - varies by process, material, thickness
        const baseSpeed: Record<SheetMetalCuttingProcess, number> = {
            sheet_metal_laser: 3000,
            sheet_metal_plasma: 2000,
            sheet_metal_waterjet: 500,
            sheet_metal_punching: 5000, // hits/min * avg distance
        };
        let speed = baseSpeed[process] || 1000;

        // Thickness factor: speed drops with thickness
        const thicknessFactor = Math.max(0.1, 1 - (thickness - 1) * 0.05);
        speed *= thicknessFactor;

        // Material factor
        if (material.category === 'ferrous_stainless') speed *= 0.6;
        else if (material.category === 'non_ferrous_aluminum') speed *= 1.3;
        else if (material.category === 'non_ferrous_titanium') speed *= 0.3;
        else if (material.category === 'non_ferrous_copper') speed *= 0.8;

        return Math.max(50, speed);
    }

    private getPierceCost(process: SheetMetalCuttingProcess, thickness: number): number {
        const baseCost: Record<SheetMetalCuttingProcess, number> = {
            sheet_metal_laser: 0.10,
            sheet_metal_plasma: 0.15,
            sheet_metal_waterjet: 0.20,
            sheet_metal_punching: 0.02,
        };
        return (baseCost[process] || 0.10) * (1 + thickness * 0.05);
    }

    private getBendTime(thickness: number, material: MaterialProperties): number {
        // Base 0.5 min per bend, more for thick/hard materials
        let time = 0.5;
        if (thickness > 3) time += (thickness - 3) * 0.2;
        if (material.category === 'ferrous_stainless') time *= 1.3;
        return time;
    }

    private getSurfaceTreatmentCost(treatment: string, surfaceAreaCm2: number): number {
        const ratePerCm2: Record<string, number> = {
            painting: 0.005,
            powder_coating: 0.008,
            plating: 0.015,
        };
        return surfaceAreaCm2 * (ratePerCm2[treatment] || 0.005);
    }

    private getCuttingProcessName(p: SheetMetalCuttingProcess): string {
        const n: Record<SheetMetalCuttingProcess, string> = { sheet_metal_laser: 'Laser cutting', sheet_metal_plasma: 'Plasma cutting', sheet_metal_waterjet: 'Waterjet cutting', sheet_metal_punching: 'CNC Punching' };
        return n[p] || p;
    }
    private getCuttingProcessNameTR(p: SheetMetalCuttingProcess): string {
        const n: Record<SheetMetalCuttingProcess, string> = { sheet_metal_laser: 'Lazer kesim', sheet_metal_plasma: 'Plazma kesim', sheet_metal_waterjet: 'Su jeti kesim', sheet_metal_punching: 'CNC Zimbalama' };
        return n[p] || p;
    }
    private getSurfaceTreatmentNameTR(t: string): string {
        const n: Record<string, string> = { painting: 'Boyama', powder_coating: 'Toz boya', plating: 'Kaplama' };
        return n[t] || t;
    }
}
